import type { BookPlanId } from '@kitup/book-schema';
import {
  brandName,
  formatPrice,
  legalLinkLabel,
  type BrandConfig,
  type LegalConfig,
} from '@kitup/brand';

/**
 * The storefront's copy in the two interface languages MyLore offers.
 *
 * Moved here verbatim from the shared locale dictionaries, where it used to live
 * while the landing and pricing pages were shared. The plan features repeat the
 * ones the book builder shows; the builder keeps its own in the shared dictionary.
 */
export interface StorefrontCopy {
  readonly name: string;
  readonly auth: { readonly signIn: string; readonly signUp: string; readonly signOut: string };
  readonly landing: {
    readonly skipToContent: string;
    readonly navLabel: string;
    readonly hero: {
      readonly eyebrow: string;
      readonly title: string;
      readonly lead: string;
      readonly cta: string;
      readonly secondaryCta: string;
      readonly note: string;
    };
    readonly showcase: { readonly videoLabel: string; readonly caption: string };
    readonly steps: {
      readonly title: string;
      readonly items: readonly { readonly title: string; readonly text: string }[];
    };
    readonly samples: {
      readonly title: string;
      readonly hint: string;
      readonly items: readonly { readonly title: string; readonly caption: string }[];
    };
    readonly final: { readonly title: string; readonly text: string; readonly cta: string };
  };
  readonly pricing: {
    readonly nav: string;
    readonly home: string;
    readonly title: string;
    readonly lead: string;
    readonly oneTime: string;
    readonly popular: string;
    readonly cta: string;
    readonly freePreview: string;
    readonly delivery: string;
    readonly taxes: string;
    readonly legalPrefix: string;
    readonly legalLink: string;
  };
  readonly plans: Readonly<Record<BookPlanId, { readonly name: string; readonly summary: string }>>;
  readonly features: {
    readonly pages: (count: number) => string;
    readonly characters: (count: number) => string;
    readonly props: (things: number, locations: number) => string;
    readonly resolution: (value: string) => string;
    readonly video: (resolution: string) => string;
    readonly noVideo: string;
    readonly narration: string;
    readonly noNarration: string;
    readonly share: string;
    readonly printStandard: string;
    readonly printHigh: string;
    readonly noPrint: string;
    readonly replacements: (count: number, withVideo: boolean) => string;
  };
}

function plural(count: number, one: string, few: string, many: string): string {
  const rule = new Intl.PluralRules('ru-RU').select(count);
  if (rule === 'one') return one;
  if (rule === 'few') return few;
  return many;
}

function ru(brand: BrandConfig, legal: LegalConfig): StorefrontCopy {
  return {
    name: brandName(brand, 'ru'),
    auth: { signIn: 'Войти', signUp: 'Регистрация', signOut: 'Выйти' },
    landing: {
      skipToContent: 'Перейти к содержимому',
      navLabel: 'Навигация аккаунта',
      hero: {
        eyebrow: 'Конструктор детских книг',
        title: 'Книга, в которой главный герой — ваш ребёнок',
        lead: 'Загрузите три фотографии и расскажите, о чём история. Дальше дело за нами: герой, развороты, обложка и файл для типографии.',
        cta: 'Собрать книгу',
        secondaryCta: 'Полистать пример',
        note: `Первый разворот бесплатно. Полная книга — от ${formatPrice(brand.planPrices.book, brand, 'ru')}.`,
      },
      showcase: {
        videoLabel: 'Пример оживлённого разворота персональной книги',
        caption: 'Фотографии героев, любимых игрушек и комнаты превращаются в оживлённый разворот.',
      },
      steps: {
        title: 'Три шага до готовой книги',
        items: [
          {
            title: 'Расскажите о герое',
            text: 'Имя, возраст, характер и три фотографии. Мы нарисуем героя в выбранном стиле, а вы решите, похож ли он.',
          },
          {
            title: 'Придумайте историю',
            text: 'Пара фраз своими словами — и история разложится по разворотам. Или напишите текст сами, до последней строки.',
          },
          {
            title: 'Заберите книгу',
            text: 'Во всех тарифах есть иллюстрации, обложка, семейная ссылка и макет для печати. Видео, озвучка и качество файлов зависят от выбранного тарифа.',
          },
        ],
      },
      samples: {
        title: 'Как выглядят развороты',
        hint: 'Три варианта компоновки текста и иллюстрации в готовой книге.',
        items: [
          {
            title: 'Текст и иллюстрация',
            caption: 'Половина разворота под картинку, половина под текст',
          },
          { title: 'На весь разворот', caption: 'Иллюстрация во всю ширину, текст поверх неё' },
          {
            title: 'Панорама с полосой',
            caption: 'Широкая картинка сверху, спокойная полоса текста снизу',
          },
        ],
      },
      final: {
        title: 'Начнём?',
        text: 'Первая книга собирается за один вечер, а перечитывают её годами.',
        cta: 'Создать книгу',
      },
    },
    pricing: {
      nav: 'Тарифы',
      home: 'На главную',
      title: 'Тарифы и цены',
      lead: 'Выберите формат персональной электронной книги. Цена фиксирована и показывается до оплаты.',
      oneTime: 'Разовая оплата · Без подписки',
      popular: 'Популярный выбор',
      cta: 'Создать книгу',
      freePreview:
        'Первый разворот создаётся бесплатно. Вы оплачиваете книгу только после просмотра результата.',
      delivery:
        'После оплаты готовая книга появится в личном кабинете не позднее 3 рабочих дней. Состав файлов и возможностей зависит от выбранного тарифа.',
      taxes: brand.priceNotes['ru'] ?? brand.priceNotes[brand.defaultLocale] ?? '',
      legalPrefix: 'Подробнее об оплате, получении и возвратах —',
      legalLink: legalLinkLabel(brand, legal, 'pricingRules', 'ru'),
    },
    plans: {
      book: {
        name: 'Книга',
        summary: 'Классическая персональная книга для чтения с экрана и печати.',
      },
      live_book: {
        name: 'Живая книга',
        summary: 'Персональная книга с ожившими иллюстрациями.',
      },
      gift_book: {
        name: 'Книга-подарок',
        summary: 'Максимальная версия с озвучкой и макетом высокого качества для печати.',
      },
    },
    features: {
      pages: (count) => `${count} ${plural(count, 'страница', 'страницы', 'страниц')}`,
      characters: (count) => `До ${count} ${plural(count, 'героя', 'героев', 'героев')}`,
      props: (things, locations) =>
        `До ${things} ${plural(things, 'вещи', 'вещей', 'вещей')} и ${locations} ${plural(locations, 'локации', 'локаций', 'локаций')}`,
      resolution: (value) =>
        value === '2K' ? 'Иллюстрации повышенного качества' : 'Иллюстрации стандартного качества',
      video: (resolution) =>
        resolution === '1080p' ? 'Видео повышенного качества' : 'Видео стандартного качества',
      noVideo: 'Без видео',
      narration: 'Озвучка голосом близкого',
      noNarration: 'Без озвучки',
      share: 'PDF для экрана и семейная ссылка',
      printStandard: 'Макет для печати в обычном качестве',
      printHigh: 'Макет для печати в отличном качестве — подходит для книг А4',
      noPrint: 'Без макета для печати',
      replacements: (count, withVideo) =>
        `${count} ${plural(count, 'бесплатная замена', 'бесплатные замены', 'бесплатных замен')} иллюстраций${withVideo ? ' и видео' : ''}`,
    },
  };
}

const number = (value: number): string => value.toLocaleString('en-US');
const noun = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

function en(brand: BrandConfig, legal: LegalConfig): StorefrontCopy {
  return {
    name: brandName(brand, 'en'),
    auth: { signIn: 'Sign in', signUp: 'Sign up', signOut: 'Sign out' },
    landing: {
      skipToContent: 'Skip to content',
      navLabel: 'Account navigation',
      hero: {
        eyebrow: 'Children’s book creator',
        title: 'A book where your child is the hero',
        lead: 'Upload three photos and tell us what the story is about. We’ll create the character, spreads, cover, and print-ready file.',
        cta: 'Create a book',
        secondaryCta: 'Browse an example',
        note: `Your first spread is free. A complete book starts at ${formatPrice(brand.planPrices.book, brand, 'en')}.`,
      },
      showcase: {
        videoLabel: 'An animated spread from a personalized book',
        caption:
          'Photos of the characters, favorite toys, and room become an animated story spread.',
      },
      steps: {
        title: 'Three steps to your finished book',
        items: [
          {
            title: 'Tell us about the hero',
            text: 'Add a name, age, personality, and three photos. We’ll draw the character in your chosen style, and you decide whether it looks right.',
          },
          {
            title: 'Create the story',
            text: 'Write a few sentences in your own words and we’ll turn them into book spreads. Or write every line yourself.',
          },
          {
            title: 'Get your book',
            text: 'Every plan includes illustrations, covers, a family link, and a print layout. Video, narration, and file quality depend on your plan.',
          },
        ],
      },
      samples: {
        title: 'What the spreads look like',
        hint: 'Three ways text and illustrations can be arranged in the finished book.',
        items: [
          { title: 'Text and illustration', caption: 'Half illustration, half text' },
          { title: 'Full spread', caption: 'A full-width illustration with text on top' },
          { title: 'Panorama with a band', caption: 'A wide image above a calm text band' },
        ],
      },
      final: {
        title: 'Shall we begin?',
        text: 'The first book takes one evening to make and years to reread.',
        cta: 'Create a book',
      },
    },
    pricing: {
      nav: 'Pricing',
      home: 'Home',
      title: 'Plans and pricing',
      lead: 'Choose your personalized digital book format. The price is fixed and shown before payment.',
      oneTime: 'One-time payment · No subscription',
      popular: 'Most popular',
      cta: 'Create a book',
      freePreview: 'Your first spread is free. You pay only after previewing the result.',
      delivery:
        'After payment, your finished book will appear in your account within 3 business days. Files and features depend on the selected plan.',
      taxes: brand.priceNotes['en'] ?? brand.priceNotes[brand.defaultLocale] ?? '',
      legalPrefix: 'Read more about payment, delivery, and refunds in the',
      legalLink: legalLinkLabel(brand, legal, 'pricingRules', 'en'),
    },
    plans: {
      book: {
        name: 'Book',
        summary: 'A classic personalized book for screen reading and printing.',
      },
      live_book: {
        name: 'Living Book',
        summary: 'A personalized book with animated illustrations.',
      },
      gift_book: {
        name: 'Gift Book',
        summary: 'The complete edition with narration and a high-quality print layout.',
      },
    },
    features: {
      pages: (count) => `${number(count)} ${noun(count, 'page', 'pages')}`,
      characters: (count) => `Up to ${number(count)} ${noun(count, 'character', 'characters')}`,
      props: (things, locations) =>
        `Up to ${number(things)} ${noun(things, 'thing', 'things')} and ${number(locations)} ${noun(locations, 'location', 'locations')}`,
      resolution: (value) =>
        value === '2K' ? 'Enhanced illustration quality' : 'Standard illustration quality',
      video: (resolution) =>
        resolution === '1080p' ? 'Enhanced-quality video' : 'Standard-quality video',
      noVideo: 'No video',
      narration: 'Narration in a familiar voice',
      noNarration: 'No narration',
      share: 'Screen PDF and family link',
      printStandard: 'Standard-quality print layout',
      printHigh: 'High-quality print layout suitable for A4 books',
      noPrint: 'No print-ready PDF',
      replacements: (count, withVideo) =>
        `${number(count)} free ${noun(count, 'redo', 'redos')} of illustrations${withVideo ? ' and videos' : ''}`,
    },
  };
}

/** Copy for the visitor's interface locale; MyLore's own default for anything else. */
export function storefrontCopy(
  brand: BrandConfig,
  legal: LegalConfig,
  locale: string,
): StorefrontCopy {
  return locale === 'en' ? en(brand, legal) : ru(brand, legal);
}
